import { useDispatch } from 'react-redux';
import { showSocialPostModal } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { SocialPost } from 'types/campaign/collaborations';
import { Influencer, SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';
import '../style.scss';

interface BestCreativeCardProps {
    post?: SocialPost;
    loading?: boolean;
    socialAccount?: SocialAccount;
    influencer?: Influencer;
    showPermalink?: boolean;
}

const TYPE_LABEL: Record<SocialPost['type'], string> = {
    REELS: 'Reel',
    FEED: 'Post',
    STORY: 'Story',
    VIDEO: 'Video',
};

const isPlayable = (post: SocialPost) =>
    post.type === 'REELS' ||
    post.type === 'VIDEO' ||
    post.type === 'STORY' ||
    post.ig?.media_type === 'VIDEO';

const BestCreativeCard = ({
    post,
    loading = false,
    socialAccount,
    influencer,
    showPermalink = true,
}: BestCreativeCardProps) => {
    const dispatch: AppDispatch = useDispatch();

    if (!post) {
        if (!loading) return null;
        return (
            <div className="application-view-best-creative application-view-best-creative--loading" aria-hidden>
                <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--title" />
                <div className="application-view-best-creative__skeleton-row">
                    <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--thumb" />
                    <div className="application-view-best-creative__skeleton-stats">
                        <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--stat" />
                        <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--stat" />
                        <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--stat" />
                        <div className="application-view-best-creative__skeleton application-view-best-creative__skeleton--stat" />
                    </div>
                </div>
            </div>
        );
    }

    const caption = post.ig?.caption?.replace(/\s+/g, ' ').trim();
    const account = post.social_account ?? socialAccount;
    const typeLabel = TYPE_LABEL[post.type];

    const openPost = () => {
        if (!post.uuid || !account) return;
        dispatch(showSocialPostModal({
            postId: post.uuid,
            influencer,
            socialAccount: account,
            showPermalink,
        }));
    };

    return (
        <section className="application-view-best-creative">
            <div className="application-view-best-creative__header">
                <h3 className="application-view-best-creative__title">
                    <i className="ri-sparkling-2-fill" aria-hidden />
                    Best Creative
                </h3>
                {typeLabel && <span className="application-view-best-creative__type">{typeLabel}</span>}
            </div>
            <button type="button" className="application-view-best-creative__body" onClick={openPost}>
                <div className="application-view-best-creative__thumb">
                    {post.thumbnail_url ? (
                        <img
                            className="application-view-best-creative__img"
                            src={post.thumbnail_url}
                            alt="Best creative"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <span className="application-view-best-creative__placeholder" aria-hidden>
                            <i className="ri-image-2-line" />
                        </span>
                    )}
                    {isPlayable(post) && (
                        <span className="application-view-best-creative__play" aria-hidden>
                            <i className="ri-play-fill" />
                        </span>
                    )}
                </div>
                <div className="application-view-best-creative__meta">
                    <div className="application-view-best-creative__stats">
                        <div className="application-view-best-creative__stat">
                            <span className="application-view-best-creative__stat-label">
                                <i className="ri-eye-line" aria-hidden />
                                Views
                            </span>
                            <span className="application-view-best-creative__stat-value">{formatNumber(post.views_count)}</span>
                        </div>
                        <div className="application-view-best-creative__stat">
                            <span className="application-view-best-creative__stat-label">
                                <i className="ri-heart-3-line" aria-hidden />
                                Likes
                            </span>
                            <span className="application-view-best-creative__stat-value">{formatNumber(post.like_count)}</span>
                        </div>
                        <div className="application-view-best-creative__stat">
                            <span className="application-view-best-creative__stat-label">
                                <i className="ri-chat-3-line" aria-hidden />
                                Comments
                            </span>
                            <span className="application-view-best-creative__stat-value">{formatNumber(post.comments_count)}</span>
                        </div>
                        {post.engagement_score && (
                            <div className="application-view-best-creative__stat">
                                <span className="application-view-best-creative__stat-label">
                                    <i className="ri-pulse-line" aria-hidden />
                                    Engg.
                                </span>
                                <span className="application-view-best-creative__stat-value">{post.engagement_score}</span>
                            </div>
                        )}
                    </div>
                    {post.collab?.handle && (
                        <p className="application-view-best-creative__collab">with @{post.collab.handle.replace(/^@/, '')}</p>
                    )}
                    {caption && <p className="application-view-best-creative__caption">{caption}</p>}
                    {post.posted_on && <p className="application-view-best-creative__date">{post.posted_on}</p>}
                </div>
            </button>
        </section>
    );
};

export default BestCreativeCard;
